import { useState, useEffect } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useDrop } from "react-dnd"
import { FiCheck, FiPlay, FiPause, FiAlertTriangle } from "react-icons/fi"
import { FaMapMarkerAlt } from "react-icons/fa"
import { GiFactory } from "react-icons/gi"
import { FiPlusCircle } from "react-icons/fi"
import { IoIosArrowUp, IoIosArrowDown } from "react-icons/io";
import "./index.scss"

import { Popup } from "components"
import { ActionConfig } from "components/Config"
import Button from "react-bootstrap/Button"
import isOnMobile from "utils/scripts/isOnMobile"

import { DateTime } from "luxon"
import { chooseOptimalColor, optimalColor } from "utils/scripts/schedule"
import dragTypes from "utils/scripts/dragTypes"
import useCustomDrag from "utils/scripts/useCustomDrag"

import { projectActions, projects as projectState, standardActions, visual, limsReviews, limsChangeControl } from "state_management"
import { FilterActionsConfig, UnplannedToPlannedConfig } from "components/Config"
import { Text } from "components/Form"
import useT from "utils/hooks/useT"
import StandardActions from "./StandardActions"
import ReviewsSection from "./ReviewsSection"
import ProjectConfigurationPage from "components/Config/ProjectConfigurationPage"

// The To Do list in team-plan
export default function ProjectTasksMenu() {
  const { t } = useT("common")
  const projects = useSelector((state) => state.projects)
  const unplannedProjectActions = useSelector((state) => state.projectActions.unplannedProjectActions)
  const allStandardActionTemplates = useSelector((state) => state.standardActions.standardActions)
  const allStandardActions = allStandardActionTemplates.reduce((acc, template) => {
    return acc.concat(template.actions.map(action => ({ ...action, color: template.color })));
  }, []);
  const [activeActionId, setActiveActionId] = useState(null)
  const activeAction = useSelector(
    (state) => (activeActionId && state.projectActions.unplannedProjectActions[activeActionId]) || null
  )

  const [createSingleAction, setCreateSingleAction] = useState(null)
  const [createStandardAction, setCreateStandardAction] = useState(null)
  const [activeStandardTaskId, setActiveStandardTaskId] = useState(null)
  const activeStandardAction = useSelector((_state) => allStandardActions.find((action) => action.id === activeStandardTaskId) || null)
  const showStandardActions = useSelector((state) => state.visual.teamPlan.showStandardActions)
  const showLims = useSelector((state) => state.teamStatus.showLims)
  // Project ids backing review actions — excluded from Unplanned Projects so a review does not
  // double-show there as a single-action project (it lives in the Reviews section instead).
  const reviewProjectIds = useSelector(limsReviews.selectReviewProjectIds)
  const isViewOnly = useSelector((state) => state.auth.isViewOnly)
  const [activeProjectId, setActiveProjectId] = useState(null);
  const [showFilterConfig, setShowFilterConfig] = useState(false)
  const [unplannedToPlannedConfig, setUnplannedToPlannedConfig] = useState({ res: null, id: null })
  const filterSettings = useSelector((state) => state.visual.filterSettings)
  const [textFilter, setTextFilter] = useState("")
  const dispatch = useDispatch()
  const [editProject, setEditProject] = useState(false);

  // RR3 #2 (follow-up): the To-Do panel is the consumer of review-link records — both for the
  // Reviews inbox and for excluding review-backing single-action projects from Unplanned Projects.
  // Load them on mount, like Sample View does for itself, so it never depends on the central
  // bootstrap's timing. Without this, a direct team-plan load where the links weren't fetched leaves
  // the review projects showing here as raw UUID-named cards (their single-action project name IS the
  // action UUID).
  useEffect(() => {
    if (showLims) {
      dispatch(limsReviews.fetch())
      // SPEC-058 B: the master-data approvals the viewer can sign (ReviewsSection's second list).
      dispatch(limsChangeControl.fetch())
    }
  }, [showLims, dispatch])

  const [{ isOver }, drop] = useDrop( () => ({
    accept: dragTypes.ACTION,
    // SPEC-066 (#4180): dropping a planned action here unplans it — never for a view-only member.
    canDrop: () => !isViewOnly,
    collect: (mon) => ({
      isOver: !!mon.isOver() && mon.canDrop(),
    }),
    drop: ({ item }) => {
      dispatch(projectActions.removePlanned(item.id))
    },
  }), [dragTypes.ACTION, isViewOnly])

  const renderProjects = () => {
    // Get unique project IDs from unplanned actions, excluding review-linked projects (those
    // surface in the Reviews section instead, so they must not double-show here).
    const projectIds = new Set();
    Object.values(unplannedProjectActions).forEach(action => {
      if (!reviewProjectIds.has(action.projectId)) {
        projectIds.add(action.projectId);
      }
    });

    // Filter projects based on text search if needed
    let filteredProjectIds = [...projectIds];
    if (textFilter) {
      const searchText = textFilter.toLowerCase();
      filteredProjectIds = filteredProjectIds.filter(id => {
        const project = projects[id];
        return project && (project.name.toLowerCase().includes(searchText) ||
          project.description?.toLowerCase().includes(searchText));
      });
    }

    // Sort projects by deadline and priority
    return filteredProjectIds
      .sort((idA, idB) => {
        const projectA = projects[idA];
        const projectB = projects[idB];

        // Sort by deadline first
        if (projectA.deadline && projectB.deadline) {
          if (projectA.deadline < projectB.deadline) return -1;
          if (projectA.deadline > projectB.deadline) return 1;
        } else if (projectA.deadline) return -1;
          else if (projectB.deadline) return 1;

        // Then by priority
        if (projectA.priority < projectB.priority) return -1;
        if (projectA.priority > projectB.priority) return 1;

        // Finally by name
        return projectA.name.localeCompare(projectB.name);
      })
      .map((projectId) => {
        const project = projects[projectId];
        const projectActions = Object.values(unplannedProjectActions).filter(
          action => action.projectId === projectId
        );

        return (
          <ProjectItem
            key={projectId}
            project={project}
            actionCount={projectActions.length}
            onClick={() => {
              // SPEC-066 (#4180): view-only opens the same dialog read-only (every field locked,
              // Close only) — "see further information", never edit.
              setEditProject(true);
              setActiveProjectId(projectId)
            }}
          />
        );
      });
  }

  return (
    <>
      {editProject && (
        <Popup
          className="Edit-Create-Project"
          onCancel={() => {
            setEditProject(false)
            setActiveProjectId(null)
          }}
        >
          <ProjectConfigurationPage
            isCreateOrEditProject={true}
            readOnly={isViewOnly}
            id={activeProjectId} // Only add id when we're editing an existing project
            setEditProject={() => {
              setEditProject(false)
              setActiveProjectId(null) // Clear the active project when done
            }}
          />
        </Popup>
      )}
      {activeActionId && unplannedProjectActions[activeActionId] && (
        <Popup nonScrollable onCancel={() => setActiveActionId(null)}>
          <ActionConfig
            title={t("editAction")}
            action={unplannedProjectActions[activeActionId]}
            cancelHandler={() => setActiveActionId(null)}
            deleteHandler={
              projects[activeAction.projectId] && projects[activeAction.projectId].singleActionProject
                ? () => {
                    dispatch(projectState.remove(activeAction.projectId))
                    setActiveActionId(null)
                  }
                : null
            }
            isSingleAction={projects[activeAction.projectId] && projects[activeAction.projectId].singleActionProject}
            submitHandler={(res) => {
              if (res.stage === "PLANNED") {
                setUnplannedToPlannedConfig({ res, id: activeActionId })
                return
              }
              dispatch(projectActions.editUnplanned(activeActionId, res)).then(() => setActiveActionId(null))
            }}
            projectId={!isOnMobile ? activeAction.projectId : null}
          />
        </Popup>
      )}
      {createSingleAction && (
        <Popup nonScrollable onCancel={() => setCreateSingleAction(null)}>
          <ActionConfig
            title={t("createAction")}
            isSingleAction
            action={{}}
            cancelHandler={() => setCreateSingleAction(null)}
            submitHandler={(res) => {
              dispatch(projectActions.createSingleAction({ ...res, tempId: "123456789" })).then(() =>
                setCreateSingleAction(null)
              )
            }}
          />
        </Popup>
      )}
      {unplannedToPlannedConfig.res && (
        <Popup
          nonScrollable
          onCancel={() => {
            setActiveActionId(unplannedToPlannedConfig.id)
            setUnplannedToPlannedConfig({ res: null, id: null })
          }}
        >
          <UnplannedToPlannedConfig
            cancelHandler={() => {
              setActiveActionId(unplannedToPlannedConfig.id)
              setUnplannedToPlannedConfig({ res: null, id: null })
            }}
            cancelAll={() => {
              setActiveActionId(null)
              setUnplannedToPlannedConfig({ res: null, id: null })
            }}
            task={unplannedProjectActions[unplannedToPlannedConfig.id]}
            res={unplannedToPlannedConfig.res}
          />
        </Popup>
      )}

      {activeStandardTaskId && activeStandardAction && (
        <Popup
          nonScrollable
          onCancel={() => setActiveStandardTaskId(null)}
        >
          <ActionConfig
            title={t("editStandardAction")}
            action={activeStandardAction}
            disabled={isViewOnly}
            cancelHandler={() => setActiveStandardTaskId(null)}
            submitHandler={(res) => {
              dispatch(standardActions.editStandardAction(activeStandardTaskId, res)).then(() => {
                setActiveStandardTaskId(null)
              })
            }}
            deleteHandler={isViewOnly ? null : () => {
              dispatch(standardActions.deleteStandardAction(activeStandardTaskId)).then(() => {
                setActiveStandardTaskId(null)
              })
            }}
            projectId={!isOnMobile ? activeStandardAction.projectId : null}
            deleteTitle={t("delete")}
          />
        </Popup>
      )}

      {createStandardAction && (
        <Popup nonScrollable onCancel={() => setCreateStandardAction(null)}>
          <ActionConfig
            title={t("createStandardAction")}
            isSingleAction
            isStandardAction
            action={{}}
            cancelHandler={() => setCreateStandardAction(null)}
            submitHandler={(res) => {
              dispatch(standardActions.createStandardActions({...res, tempId: "123456789" })).then(() =>
                setCreateStandardAction(null)
              )
            }}
          />
        </Popup>
      )}

      <div
        className="ProjectTasksMenu-main-container"
        ref={drop}
        style={{
          filter: isOver ? "brightness(50%)" : "none",
        }}
      >
        <div className="standard-action-container">
          <div className="standard-action-dropdown" onClick={() => dispatch(visual.setShowStandardActions(!showStandardActions))}>
            <h5>{t("standardActions")}</h5>
            {showStandardActions ? <IoIosArrowUp/> : <IoIosArrowDown/>}
          </div>
          {!isViewOnly && (
            <Button
              title={t("createStandardAction")}
              onClick={() => setCreateStandardAction(true)}
            >
              <FiPlusCircle/>
            </Button>
          )}
        </div>

        {showStandardActions && (
          <div>
            <StandardActions setActiveStandardTaskId={setActiveStandardTaskId}/>
          </div>
        )}

        <hr/>
        {showLims && <ReviewsSection />}
        <div className="ProjectTasksMenu-tasks-container">
          {showFilterConfig && <FilterActionsConfig cancelHandler={() => setShowFilterConfig(false)}></FilterActionsConfig>}
          <div className="FilterButtons-Container">
            <h5 className="Pheading ActionList-Caption">{t("unplannedProjects")}</h5>
            <div className="todoList-Buttons">
              {!isViewOnly && (
                <Button
                  className="Filter-button"
                  title={t("createNewProject")}
                  variant="primary"
                  onClick={() => setEditProject(true)}
                >
                  <p>{t("createNewProject")}</p>
                </Button>
              )}
            </div>
            <div className="Todolist-Textfilter-Filterlist">
              <Text value={textFilter} onChange={setTextFilter} placeholder={t("filterByText")}></Text>
              <Button className="Filter-button" onClick={() => setShowFilterConfig(true)}>
                <p>{t("filter")}</p>
              </Button>
            </div>
              <ul className="list-unstyled">
                {filterSettings ? filterSettings.isDeadlineFilter && <li>{t("byDeadline")}</li> : ""}
                {filterSettings ? filterSettings.isCompetenceGroupFilter && <li>{t("byGroup")}</li> : ""}
                {textFilter ? <li>{t("byText")}</li> : ""}
                {filterSettings ? filterSettings.scrumViewOrder && <li>{t("scrumViewOrdering")}</li> :""}
              </ul>
          </div>
          {projects && renderProjects()}
        </div>
      </div>
    </>
  )
}

function ProjectItem({ project, actionCount, onClick }) {
  const { t } = useT("common")
  const teamTimeZone = useSelector((state) => state.teamInfo.timezone);
  const unplannedProjectActions = useSelector((state) => state.projectActions.unplannedProjectActions);

  const actionsWithWarnings = Object.values(unplannedProjectActions)
    .filter(action => action.projectId === project.id && action.iapWarning && action.iapWarning.length > 0);

  const warningMessage = actionsWithWarnings.length > 0 ?
    actionsWithWarnings.map(action => `${action.name}: ${action.iapWarning}`).join('\n') : '';

  const { isDragging, dragRef } = useCustomDrag(dragTypes.PROJECT, project);
  // SPEC-066 (#4180): view-only sees and clicks the card; the drag handle is never attached.
  const isViewOnly = useSelector((state) => state.auth.isViewOnly)

  return (
    <div
      ref={isViewOnly ? undefined : dragRef}
      className="ProjectTasksMenu-analysis-container"
      onClick={onClick}
      style={{
        opacity: isDragging ? "0.4" : undefined,
        backgroundColor: project.color,
        color: chooseOptimalColor(project.color),
        cursor: "pointer",
        boxShadow: project.deadline &&
          DateTime.fromISO(project.deadline).setZone(teamTimeZone).startOf("day") <
            DateTime.local().setZone(teamTimeZone).startOf("day")
          ? "0px 0px 0px 6px red"
          : "",
      }}
    >
      <div className="ProjectTasksMenu-text">
        <div className="flex-container">
          <h5>{project.name}</h5>
        </div>
        <p>
          {t("deadlineColon")} {project.deadline
            ? DateTime.fromISO(project.deadline).setZone(teamTimeZone).toFormat("ff")
            : t("none")}
        </p>
        <p>{t("actionsColon")} {actionCount}</p>
      </div>

      {actionsWithWarnings.length > 0 && (
        <div title={warningMessage} className="hoverable-container">
          <div className="project-yellow-warning-container">
            <FiAlertTriangle />
          </div>
        </div>
      )}
    </div>
  );
}
// `colorOverride` (RR8 #4060): a review/supervisor/QA card is backed by a single-action project
// whose own colour is unset, so it rendered white next to the dark-green analysis it was made
// from. The Reviews section passes the SOURCE ANALYSIS's colour so the card matches its origin.
// Omitted everywhere else, which keeps the project colour as before.
export function UnplannedProjectActionItem({ action, onClick, colorOverride = null }) {
  const { t } = useT("common")
  const projects = useSelector((state) => state.projects)
  const groups = useSelector((state) => state.competenceGroups)
  const teamTimeZone = useSelector((state) => state.teamInfo.timezone)
  const dispatch = useDispatch()
  const allStandardActions = useSelector((state) => state.standardActions.standardActions)


  const project = action.isStandardAction
  ? allStandardActions.find((standardAction) =>
  standardAction.actions.some((actionItem) => actionItem.id === action.id)
  )
  : projects[action.projectId];

  const { isDragging, dragRef } = useCustomDrag(action.isStandardAction ? dragTypes.STANDARD_ACTION : dragTypes.ACTION, action)
  // SPEC-066 (#4180): view-only sees and clicks the card; no drag handle, and the on-site toggle
  // below (a write) is rendered without its click handler.
  const isViewOnly = useSelector((state) => state.auth.isViewOnly)
  const toggleOnSite = isViewOnly
    ? undefined
    : (e) => {
        dispatch(projectActions.setLocationUnplanned(action.id, true))
        e.stopPropagation()
      }

  // RR8 #4060: the source analysis colour wins when supplied; otherwise the project colour as before.
  const cardColor = colorOverride || (project && project.color)

  // This is conditionally rendered, so when unplanning a standard action, it doesn't throw an error because the actions projectId is also removed from the database
  if (project) return (
    <div
      ref={isViewOnly ? undefined : dragRef}
      className={`ProjectTasksMenu-analysis-container` + (action.isStandardAction ? "-standard-action" : "")}
      onClick={onClick}
      style={{
        opacity: isDragging ? "0.4" : undefined,
        backgroundColor: cardColor,
        cursor: "pointer",
        color: chooseOptimalColor(cardColor),
        boxShadow:
          action.isStandardAction ?
          "0px 0px 0px 0px #000000"
          :
          action.deadline || project.deadline
            ? (action.deadline && DateTime.fromISO(action.deadline).setZone(teamTimeZone).startOf("day") < DateTime.local().setZone(teamTimeZone).startOf("day")) ||
              (!action.deadline &&
                project.deadline &&
                DateTime.fromISO(project.deadline).setZone(teamTimeZone).startOf("day") < DateTime.local().setZone(teamTimeZone).startOf("day"))
              ? "0px 0px 0px 6px red"
              : ""
            : "",
      }}
    >
      {!action.isStandardAction &&
        <div className="UnplannedProjectAction-Onsite">
          {!action.onSite && (
            <FaMapMarkerAlt
              title={t("canBePerformedAnywhere")}
              onClick={toggleOnSite}
            />
          )}
          {action.onSite && (
            <GiFactory
              title={t("canOnlyBePerformedOnSite")}
              color={optimalColor(project.color,"blue","orange")}
              onClick={toggleOnSite}
            />
          )}
        </div>
      }
      {action.state === "STARTED" ? (
        <FiPlay />
      ) : action.state === "PAUSED" ? (
        <FiPause />
      ) : action.state === "STOPPED" ? (
        <FiCheck />
      ) : null}
      <div className="ProjectTasksMenu-text">
        <div className="flex-container">
          {project.singleActionProject ? null : <h5 style={{margin:"0"}}>{project.name + ":"}&nbsp;</h5>}
          <h5>{action.name}</h5>
        </div>
        {!action.isStandardAction && (
          <>
            <p>
              {t("deadlineColon")}{" "}
              {action.deadline
                ? DateTime.fromISO(action.deadline).setZone(teamTimeZone).toFormat("ff")
                : project.deadline
                ? DateTime.fromISO(project.deadline).setZone(teamTimeZone).toFormat("ff")
                : t("none")}
            </p>
            <CompetenceGroupSummary allowedGroupIds={action.allowedGroupIds} groups={groups} t={t} />
          </>
        )}
      </div>
      {action.iapWarning && action.iapWarning.length > 0 && (
        <div title={action.iapWarning} className="hoverable-container">
          <div className="project-yellow-warning-container">
            <FiAlertTriangle />
          </div>
        </div>
      )}
    </div>
  )
}

// How many competence-group names to show inline on an action card before collapsing the rest
// into a "+N more" affordance. The full (deduplicated, sorted) list is always available in the
// hover tooltip.
const COMPETENCE_GROUPS_INLINE_LIMIT = 2

// Compact competence-group line for an action card (K4 / PRD-006). A review/correction with no
// Review-Specification group falls back to ALL competence groups, and the test DB contains many
// groups whose names share a "(ABBR)" suffix — rendering the full register inline produced a huge,
// run-together blob (the "(DBET)DBET)…" finding). Here we DEDUPE the ids (a stale/duplicated id
// list must never repeat a name), resolve only KNOWN groups, sort by name, then show at most
// COMPETENCE_GROUPS_INLINE_LIMIT names with a "+N more" tail; the complete sorted list lives in the
// title tooltip. Renders nothing when there is no resolvable group.
function CompetenceGroupSummary({ allowedGroupIds, groups, t }) {
  const names = [...new Set(allowedGroupIds || [])]
    .filter((groupId) => groups[groupId])
    .map((groupId) => groups[groupId].name)
    .sort((a, b) => a.localeCompare(b))

  if (names.length === 0) return null

  // RR3 #11: a review/correction with no Review-Specification falls back to ALL competence groups
  // (so anyone can review). Don't enumerate the whole register — show a single "All competence
  // groups" label inline and in the tooltip, only when the allowed set genuinely covers every group.
  const totalGroups = Object.keys(groups).length
  if (totalGroups > 0 && names.length === totalGroups) {
    return (
      <p title={t("competenceGroupsAll")}>
        {t("competenceGroupsColon")}
        {t("competenceGroupsAll")}
      </p>
    )
  }

  const shown = names.slice(0, COMPETENCE_GROUPS_INLINE_LIMIT)
  const overflow = names.length - shown.length
  const inline =
    overflow > 0
      ? `${shown.join(", ")}, ${t("competenceGroupsMore", { count: overflow })}`
      : shown.join(", ")

  // Tooltip shows every group, one per line, so the full list is reachable without bloating the card.
  return (
    <p title={names.join("\n")}>
      {t("competenceGroupsColon")}
      {inline}
    </p>
  )
}
